import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../layout/Layout";
import { apiV2 } from "../../../api/axiosConfig";
import BasicSelect from "../../../components/form-v2/basic-select";
import { BlueButton } from "../../../components/ui/Buttons";
import { DepartmentSelectSingle } from "../../../components/form-v2/searchable-select/DepartmentSelect";
import { useNavigate } from "react-router-dom";
import { isAxiosError } from "axios";
import { Client, Department } from "../../../types/userManagement";
import { useAuth } from "../../../context/user";
import { ClientSelectSingle } from "../../../components/form-v2/searchable-select/ClientSelect";
import { useUserManagementBrowsing } from "../../../lib/user-management/UserManagementBrowsingContext";
import UserSelectSingleV2 from "../../../components/form-v2/searchable-select/UserSelectSingleV2";
import { UserDataTypes } from "../../../types/user-data-types";
import moment from "moment";
import BasicText from "@/components/form-v2/basic-text";
import { useTimeZoneSelectContext } from "@/providers/TimeZoneSelectProvider";

const DeviceCreate: React.FC = () => {
  const [name, setName] = useState("");
  const [deviceSource, setDeviceSource] = useState<string | undefined>("null");
  const [deviceType, setDeviceType] = useState<string | undefined>("null");
  const [deviceSourceAPI, setDeviceSourceAPI] = useState<string | undefined>("null");
  const [imei, setImei] = useState("");
  const [simCardPhoneNum, setSimCardPhoneNum] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [sourceID, setSourceID] = useState("");
  const [secondary, setSecondary] = useState(false);
  const [oemSerial, setOemSerial] = useState("");
  const [apiHubSerial, setApiHubSerial] = useState("");
  const [apiSource, setApiSource] = useState("");
  const [department, setDepartment] = useState<Department>();
  const [deviceID, setDeviceID] = useState("");
  const [useNameAsEverywhereHub, setUseNameAsEverywhereHub] = useState(false);
  const [client, setClient] = useState<Client>();
  const [selectedUser, setSelectedUser] = useState<UserDataTypes | null>(null);
  const [endDate, setEndDate] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const auth = useAuth();
  const navigate = useNavigate();
  const browsing = useUserManagementBrowsing();

  const initialClientID = useMemo(() => browsing.clientID, [browsing.clientID]);

  useEffect(() => {
    setDepartment(undefined);
  }, [client]);

  const tzCtx = useTimeZoneSelectContext();
  const timezone = tzCtx.timeZone;
  const now = moment().tz(timezone).toISOString();

  const handleUserChange = (user: UserDataTypes | null) => {
    setSelectedUser(user);
  };

  useEffect(() => {
    if (useNameAsEverywhereHub) {
      setDeviceID(name);
    }
  }, [name, useNameAsEverywhereHub]);

  const info = useMemo(() => {
    if (deviceType !== "satellite-everywhere") return [];
    return [
      { key: "imei", value: imei },
      { key: "simCardPhoneNum", value: simCardPhoneNum },
      { key: "make", value: make },
      { key: "model", value: model },
      { key: "sourceID", value: sourceID },
      { key: "secondary", value: secondary ? "yes" : "no" },
      { key: "oemSerial", value: oemSerial },
      { key: "apiHubSerial", value: apiHubSerial },
      { key: "apiSource", value: apiSource },
    ];
  }, [
    deviceType,
    deviceSourceAPI,
    imei,
    simCardPhoneNum,
    make,
    model,
    sourceID,
    secondary,
    oemSerial,
    apiHubSerial,
    apiSource,
  ]);

  const save = useCallback(async () => {
    setSaving(true);
    setErrors([]);
  
    if (!deviceID || deviceID.trim() === "") {
      setErrors(["Device ID is required"]);
      setSaving(false);
      return;
    }
  
    if (
      deviceSource === "satellite-everywhere" &&
      deviceType === "satellite-everywhere" &&
      deviceSourceAPI === "everywhere-mission-connect"
    ) {
      const imeiRegex = /^[0-9]{15}$/;
      if (!imeiRegex.test(deviceID)) {
        setErrors(["Invalid IMEI Device ID"]);
        setSaving(false);
        return;
      }
    }
  
    try {
      await apiV2.post("device-create", {
        name,
        type: deviceType,
        deviceSourceAPI,
        departmentID: department?.id,
        clientID: client?.id,
        deviceID,
        userID: selectedUser?.id,
        assignmentEndDate: selectedUser ? moment.tz(endDate, timezone).toISOString() : undefined,
        ownershipStartedAt: selectedUser ? now : undefined,
        info,
      });
    } catch (e) {
      if (isAxiosError(e) && e?.response?.data?.error) {
        setErrors([e.response.data.error]);
      } else if (isAxiosError(e) && e?.response?.data?.errors) {
        setErrors(e.response.data.errors);
      } else {
        setErrors(["An error has occurred"]);
      }
      setSaving(false);
      return;
    }
  
    setSaving(false);
    const params = new URLSearchParams();
    params.set("message", "device has been created");
    navigate(`/management/devices?${params.toString()}`);
  }, [
    name,
    deviceType,
    deviceSourceAPI,
    department,
    client,
    deviceID,
    selectedUser,
    info,
    navigate,
    endDate,
    timezone,
    now,
    deviceSource,
  ]);
  

  return (
    <Layout active="devices">
      <div className="flex flex-row gap-8 mb-8">
        <div className="flex flex-col flex-1 gap-8 mb-8">
          <div className="bg-white shadow-md p-4 flex flex-col gap-3">
            <p className="text-md text-left pb-2 font-semibold">Create Device</p>

            <BasicText value={name} onChange={setName} label="Name" required />

            <BasicSelect
              label="Source"
              value={deviceSource}
              onChange={setDeviceSource}
              required
              options={[
                { value: "null", label: "Please Select a Source" },
                { value: "satellite-everywhere", label: "Everywhere" },
              ]}
            />

            {deviceSource === "satellite-everywhere" && (
              <BasicSelect
                label="Device Type"
                value={deviceType}
                onChange={setDeviceType}
                required
                options={[
                  { value: "null", label: "Please Select a Type" },
                  { value: "satellite-everywhere", label: "Satellite Device" },
                ]}
              />
            )}

            {deviceSource === "satellite-everywhere" && deviceType === "satellite-everywhere" && (
              <BasicSelect
                label="Source API"
                value={deviceSourceAPI}
                onChange={setDeviceSourceAPI}
                required
                options={[
                  { value: "null", label: "Please Select a Type" },
                  { value: "everywhere-webhooks", label: "Everywhere Webhooks" },
                  { value: "everywhere-mission-connect", label: "Mission Connect" },
                ]}
              />
            )}

            {deviceSource === "satellite-everywhere" &&
              deviceType === "satellite-everywhere" &&
              deviceSourceAPI === "everywhere-webhooks" && (
                <BasicText
                  value={deviceID}
                  onChange={setDeviceID}
                  label="Device ID (Everywhere Device Name)"
                  required
                />
              )}

            {deviceSource === "satellite-everywhere" &&
              deviceType === "satellite-everywhere" &&
              deviceSourceAPI === "everywhere-mission-connect" && (
                <BasicText value={deviceID} onChange={setDeviceID} label="Device ID (IMEI)" required />
              )}

            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                deviceType === "satellite-everywhere" ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  deviceType === "satellite-everywhere" && deviceSource === "satellite-everywhere"
                    ? "max-h-[1000px] opacity-100"
                    : "max-h-0 opacity-0"
                }`}
              >
                <div className="bg-white-blue p-4 space-y-3 rounded-lg mt-2 mb-4">
                  <BasicText value={imei} onChange={setImei} label="IMEI" />
                  <BasicText value={simCardPhoneNum} onChange={setSimCardPhoneNum} label="SIM Card Phone Number" />
                  <BasicText value={make} onChange={setMake} label="Make" />
                  <BasicText value={model} onChange={setModel} label="Model" />
                  <BasicText value={sourceID} onChange={setSourceID} label="Source ID" />
                  <BasicSelect
                    label="Secondary"
                    value={secondary ? "yes" : "no"}
                    onChange={(v) => setSecondary(v === "yes")}
                    options={[
                      { value: "yes", label: "Yes" },
                      { value: "no", label: "No" },
                    ]}
                  />
                  <BasicText value={oemSerial} onChange={setOemSerial} label="OEM Serial" />
                  <BasicText value={apiHubSerial} onChange={setApiHubSerial} label="API Hub Serial" />
                  <BasicText value={apiSource} onChange={setApiSource} label="API Source" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-8 flex-1">
          <div className="bg-white shadow-md p-4 flex flex-col gap-8">
            <p className="text-md text-left pb-2 font-semibold">Access</p>
            {auth.isSuperAdmin && (
              <div>
                <label className="mb-1">Client</label>
                <label className="mb-1 text-dark-red"> *</label>
                <ClientSelectSingle client={client} onChange={setClient} initialClientID={initialClientID} />
              </div>
            )}
            {(client || !auth.isSuperAdmin) && (
              <div>
                <label className="mb-1">Department</label>
                <DepartmentSelectSingle department={department} onChange={setDepartment} clientID={client?.id} />
              </div>
            )}
            <p className="text-md">Assign User</p>
            <UserSelectSingleV2 user={selectedUser} onSelectionChange={handleUserChange} clientID={initialClientID} />
            {selectedUser && (
              <>
                <p className="text-md">User Assignment End Date</p>
                <BasicText value={endDate} type={"date"} onChange={setEndDate} placeholder={"End Date"} />
              </>
            )}

            <BlueButton text="Create Device" disabled={saving} onClick={save} loading={saving} />

            {errors.length > 0 && (
              <div className="flex flex-col gap-2">
                {errors.map((e, i) => (
                  <p key={i} className="text-center text-red">
                    {e}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

DeviceCreate.displayName = "DeviceCreate";

export default DeviceCreate;
